import { HashRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

import "./index.css";
import HomePage from "./pages/HomePage";
import ServiceWebPage from "./pages/ServiceWebPage";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import EmpecemosPage from "./pages/EmpecemosPage";
import DocumentationPage from "./pages/DocumentationPage";
import { LeadProvider } from "./context/LeadContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProjectsProvider } from "./context/ProjectsContext";
import ProtectedRoute from "./components/dashboard/ProtectedRoute";
import DashboardShell from "./components/dashboard/DashboardShell";
import ProjectSettingsLayout from "./components/dashboard/ProjectSettingsLayout";
import ProjectBillingGate from "./components/dashboard/ProjectBillingGate";
import ProjectToolAccessGate from "./components/dashboard/ProjectToolAccessGate";
import ProjectSettingsRedirect from "./components/dashboard/ProjectSettingsRedirect";
import LoginPage from "./pages/dashboard/LoginPage";
import RegisterPage from "./pages/dashboard/RegisterPage";
import ConfirmEmailPage from "./pages/dashboard/ConfirmEmailPage";
import ForgotPasswordPage from "./pages/dashboard/ForgotPasswordPage";
import ResetPasswordPage from "./pages/dashboard/ResetPasswordPage";
import DashboardHomePage from "./pages/dashboard/DashboardHomePage";
import AccountSecurityPage from "./pages/dashboard/AccountSecurityPage";
import AffiliateNetworkPage from "./pages/dashboard/AffiliateNetworkPage";
import ProjectsPage from "./pages/dashboard/ProjectsPage";
import ProjectProductsPage from "./pages/dashboard/ProjectProductsPage";
import ProjectAgendaPage from "./pages/dashboard/ProjectAgendaPage";
import SuperPanelPage from "./pages/dashboard/SuperPanelPage";
import SuperProjectsExplorerPage from "./pages/dashboard/SuperProjectsExplorerPage";
import SuperProject360Page from "./pages/dashboard/SuperProject360Page";
import SuperActivityPage from "./pages/dashboard/SuperActivityPage";
import SuperAnalyticsPage from "./pages/dashboard/SuperAnalyticsPage";
import UsersAdminPage from "./pages/dashboard/UsersAdminPage";
import ProjectDetailPage from "./pages/dashboard/ProjectDetailPage";
import ProjectSiteRedirect from "./components/dashboard/landingStudio/ProjectSiteRedirect";
import ProjectLandingVisualEditorPage from "./pages/dashboard/ProjectLandingVisualEditorPage";
import ProjectOrdersPage from "./pages/dashboard/ProjectOrdersPage";
import ProjectCustomersPage from "./pages/dashboard/ProjectCustomersPage";
import ProjectEcommercePage from "./pages/dashboard/ProjectEcommercePage";
import ProjectFiscalBillingPage from "./pages/dashboard/ProjectFiscalBillingPage";
import ProjectStatsPage from "./pages/dashboard/ProjectStatsPage";
import ProjectActivityPage from "./pages/dashboard/ProjectActivityPage";
import PlanBillingPage from "./pages/dashboard/PlanBillingPage";
import BillingPlansAdminPage from "./pages/dashboard/BillingPlansAdminPage";
import ProjectNotificationsPage from "./pages/dashboard/ProjectNotificationsPage";
import ProjectTablesPage from "./pages/dashboard/ProjectTablesPage";
import ProjectRealEstateLayout from "./pages/dashboard/realEstate/ProjectRealEstateLayout";
import ProjectRealEstateOverviewPage from "./pages/dashboard/realEstate/ProjectRealEstateOverviewPage";
import ProjectPropertiesPage from "./pages/dashboard/realEstate/ProjectPropertiesPage";
import ProjectLeasesPage from "./pages/dashboard/realEstate/ProjectLeasesPage";
import ProjectLeaseCollectionsPage from "./pages/dashboard/realEstate/ProjectLeaseCollectionsPage";
import SuperAdminRoute from "./components/dashboard/SuperAdminRoute";
import OwnerRoute from "./components/dashboard/OwnerRoute";
import OwnerPanelPage from "./pages/dashboard/owner/OwnerPanelPage";
import OwnerJarvisWorkspace from "./pages/dashboard/owner/OwnerJarvisWorkspace";
import OwnerJarvisLabPage from "./pages/dashboard/owner/OwnerJarvisLabPage";
import OwnerFinancePage from "./pages/dashboard/owner/OwnerFinancePage";
import OwnerMovementsPage from "./pages/dashboard/owner/OwnerMovementsPage";
import OwnerCommercialPage from "./pages/dashboard/owner/OwnerCommercialPage";
import OwnerStatisticsPage from "./pages/dashboard/owner/OwnerStatisticsPage";
import OwnerAiUsagePage from "./pages/dashboard/owner/OwnerAiUsagePage";
import OwnerCalendarPage from "./pages/dashboard/owner/OwnerCalendarPage";
import OwnerClientsPage from "./pages/dashboard/owner/OwnerClientsPage";
import OwnerProject360Page from "./pages/dashboard/owner/OwnerProject360Page";
import OwnerProjectsPage from "./pages/dashboard/owner/OwnerProjectsPage";
import OwnerRoutine360Page from "./pages/dashboard/owner/OwnerRoutine360Page";
import OwnerHabitsPage from "./pages/dashboard/owner/OwnerHabitsPage";
import OwnerFoodPage from "./pages/dashboard/owner/OwnerFoodPage";
import OwnerLifePage from "./pages/dashboard/owner/OwnerLifePage";
import ProjectErpLayout from "./pages/dashboard/erp/ProjectErpLayout";
import ProjectErpOverviewPage from "./pages/dashboard/erp/ProjectErpOverviewPage";
import ProjectErpMovementsPage from "./pages/dashboard/erp/ProjectErpMovementsPage";
import ProjectErpCashPage from "./pages/dashboard/erp/ProjectErpCashPage";
import ProjectErpLedgerPage from "./pages/dashboard/erp/ProjectErpLedgerPage";
import ProjectErpTransactionsPage from "./pages/dashboard/erp/ProjectErpTransactionsPage";
import ProjectErpStockPage from "./pages/dashboard/erp/ProjectErpStockPage";
import ProjectErpAccountsPage from "./pages/dashboard/erp/ProjectErpAccountsPage";
import WebProfitLoadingScreen from "./components/system/WebProfitLoadingScreen";
import ServiceUpdateBoundary from "./components/system/ServiceUpdateBoundary";
import ProjectErpTaxesPage from "./pages/dashboard/erp/ProjectErpTaxesPage";
import { WebProfitThemeProvider, isWebProfitPanelPath } from "./theme/webProfitTheme";

function AppContent() {
  const location = useLocation();
  const { loading: authLoading } = useAuth();
  const isPanelRoute = isWebProfitPanelPath(location.pathname);

  if (isPanelRoute && authLoading) {
    return <WebProfitLoadingScreen />;
  }

  return (
    <>
      {!isPanelRoute && <Navbar />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/servicios" element={<ServiceWebPage />} />
        <Route path="/empecemos" element={<EmpecemosPage />} />
        <Route path="/documentacion" element={<DocumentationPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/registro" element={<RegisterPage />} />
        <Route path="/confirmar-email" element={<ConfirmEmailPage />} />
        <Route path="/olvide-contrasena" element={<ForgotPasswordPage />} />
        <Route path="/restablecer-contrasena" element={<ResetPasswordPage />} />
        <Route
          path="/editor-web/:projectId"
          element={
            <ProtectedRoute>
              <ProjectToolAccessGate toolKey="landing">
                <ProjectLandingVisualEditorPage />
              </ProjectToolAccessGate>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardHomePage />} />
          <Route path="seguridad" element={<AccountSecurityPage />} />
          <Route path="referidos" element={<AffiliateNetworkPage />} />
          <Route path="owner" element={<OwnerRoute><OwnerJarvisWorkspace /></OwnerRoute>}>
            <Route index element={<OwnerPanelPage />} />
            <Route path="finanzas" element={<OwnerFinancePage />} />
            <Route path="movimientos" element={<OwnerMovementsPage />} />
            <Route path="clientes" element={<OwnerClientsPage />} />
            <Route path="proyectos" element={<OwnerProjectsPage />} />
            <Route path="proyectos/:initiativeId" element={<OwnerProject360Page />} />
            <Route path="rutinas/:routineId" element={<OwnerRoutine360Page />} />
            <Route path="vida" element={<OwnerLifePage />} />
            <Route path="habitos" element={<OwnerHabitsPage />} />
            <Route path="comida" element={<OwnerFoodPage />} />
            <Route path="operacion" element={<OwnerCommercialPage />} />
            <Route path="calendario" element={<OwnerCalendarPage />} />
            <Route path="estadisticas" element={<OwnerStatisticsPage />} />
            <Route path="uso-ia" element={<OwnerAiUsagePage />} />
            <Route path="jarvis-lab" element={<OwnerJarvisLabPage />} />
          </Route>
          <Route path="superpanel" element={<SuperAdminRoute><SuperPanelPage /></SuperAdminRoute>} />
          <Route path="superpanel/proyectos" element={<SuperAdminRoute><SuperProjectsExplorerPage /></SuperAdminRoute>} />
          <Route path="superpanel/proyectos/:projectId" element={<SuperAdminRoute><SuperProject360Page /></SuperAdminRoute>} />
          <Route path="actividad" element={<SuperAdminRoute><SuperActivityPage /></SuperAdminRoute>} />
          <Route path="analitica" element={<SuperAdminRoute><SuperAnalyticsPage /></SuperAdminRoute>} />
          <Route path="usuarios" element={<SuperAdminRoute><UsersAdminPage /></SuperAdminRoute>} />
          <Route path="billing" element={<SuperAdminRoute><BillingPlansAdminPage /></SuperAdminRoute>} />
          <Route path="proyectos" element={<ProjectsPage />} />
          <Route
            path="proyectos/:projectId/inicio"
            element={<ProjectToolAccessGate toolKey="home"><DashboardHomePage /></ProjectToolAccessGate>}
          />
          <Route path="proyectos/:projectId" element={<ProjectSettingsRedirect />} />
          <Route path="proyectos/:projectId/billing" element={<ProjectSettingsRedirect section="suscripcion" />} />
          <Route path="proyectos/:projectId/ecommerce" element={<ProjectSettingsRedirect section="ecommerce" />} />
          <Route path="proyectos/:projectId/notificaciones" element={<ProjectSettingsRedirect section="notificaciones" />} />
          <Route
            path="proyectos/:projectId/configuracion"
            element={<ProjectToolAccessGate><ProjectSettingsLayout /></ProjectToolAccessGate>}
          >
            <Route path="general" element={<ProjectBillingGate featureName="La configuración del negocio"><ProjectDetailPage embedded section="general" /></ProjectBillingGate>} />
            <Route path="pagina" element={<ProjectDetailPage embedded section="web" />} />
            <Route path="funcionalidades" element={<ProjectSettingsRedirect section="suscripcion" />} />
            <Route path="ecommerce" element={<ProjectBillingGate featureName="Los cobros de la tienda"><ProjectEcommercePage /></ProjectBillingGate>} />
            <Route path="facturacion" element={<ProjectBillingGate featureName="La facturación ARCA"><ProjectFiscalBillingPage /></ProjectBillingGate>} />
            <Route path="notificaciones" element={<ProjectBillingGate featureName="Las notificaciones"><ProjectNotificationsPage /></ProjectBillingGate>} />
            <Route path="suscripcion" element={<PlanBillingPage embedded />} />
            <Route path="accesos" element={<ProjectBillingGate featureName="Los permisos del proyecto"><ProjectDetailPage embedded section="access" /></ProjectBillingGate>} />
          </Route>
          <Route path="proyectos/:projectId/pagina" element={<ProjectToolAccessGate><ProjectSiteRedirect /></ProjectToolAccessGate>} />
          <Route path="proyectos/:projectId/productos" element={<ProjectToolAccessGate><ProjectBillingGate featureName="Productos y servicios"><ProjectProductsPage /></ProjectBillingGate></ProjectToolAccessGate>} />
          <Route path="proyectos/:projectId/agenda" element={<ProjectToolAccessGate><ProjectBillingGate featureName="La agenda de servicios"><ProjectAgendaPage /></ProjectBillingGate></ProjectToolAccessGate>} />
          <Route path="proyectos/:projectId/mesas" element={<ProjectToolAccessGate><ProjectBillingGate featureName="Mesas y reservas"><ProjectTablesPage /></ProjectBillingGate></ProjectToolAccessGate>} />
          <Route path="proyectos/:projectId/pedidos" element={<ProjectToolAccessGate><ProjectBillingGate featureName="Pedidos"><ProjectOrdersPage /></ProjectBillingGate></ProjectToolAccessGate>} />
          <Route path="proyectos/:projectId/clientes" element={<ProjectToolAccessGate><ProjectBillingGate featureName="CRM de clientes"><ProjectCustomersPage /></ProjectBillingGate></ProjectToolAccessGate>} />
          <Route
            path="proyectos/:projectId/inmobiliaria"
            element={<ProjectToolAccessGate toolKey="real-estate"><ProjectBillingGate featureName="Inmobiliaria"><ProjectRealEstateLayout /></ProjectBillingGate></ProjectToolAccessGate>}
          >
            <Route index element={<ProjectRealEstateOverviewPage />} />
            <Route path="propiedades" element={<ProjectPropertiesPage />} />
            <Route path="contratos" element={<ProjectLeasesPage />} />
            <Route path="cobranzas" element={<ProjectLeaseCollectionsPage />} />
          </Route>
          <Route
            path="proyectos/:projectId/sistema"
            element={
              <ProjectToolAccessGate>
                <ProjectBillingGate featureName="Gestión del negocio">
                  <ProjectErpLayout />
                </ProjectBillingGate>
              </ProjectToolAccessGate>
            }
          >
            <Route index element={<ProjectErpOverviewPage />} />
            <Route path="items" element={<ProjectProductsPage embedded erpMode />} />
            <Route path="movimientos" element={<ProjectErpMovementsPage />} />
            <Route path="caja" element={<ProjectErpCashPage />} />
            <Route path="ledger" element={<ProjectErpLedgerPage />} />
            <Route path="transacciones" element={<ProjectErpTransactionsPage />} />
            <Route path="stock" element={<ProjectErpStockPage />} />
            <Route path="cuentas" element={<ProjectErpAccountsPage />} />
            <Route path="impuestos" element={<ProjectErpTaxesPage />} />
          </Route>
          <Route path="proyectos/:projectId/actividad" element={<ProjectToolAccessGate><ProjectActivityPage /></ProjectToolAccessGate>} />
          <Route path="proyectos/:projectId/estadisticas" element={<ProjectToolAccessGate><ProjectBillingGate featureName="Las estadísticas"><ProjectStatsPage /></ProjectBillingGate></ProjectToolAccessGate>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isPanelRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <HashRouter>
      <WebProfitThemeProvider>
        <ServiceUpdateBoundary>
          <LeadProvider>
            <AuthProvider>
              <ProjectsProvider>
                <AppContent />
              </ProjectsProvider>
            </AuthProvider>
          </LeadProvider>
        </ServiceUpdateBoundary>
      </WebProfitThemeProvider>
    </HashRouter>
  );
}

export default App;
